<template>
  <div class="pa-0 secondary--text text--darken-3">
    <!-- Header Area -->
    <div class="bg-grey-light pa-8 border-bottom">
      <div class="d-flex align-center mb-2">
        <v-avatar color="primary" size="40" class="mr-4 shadow-sm">
          <v-icon color="white" small>fas fa-exchange-alt</v-icon>
        </v-avatar>
        <div>
          <div class="text-h5 font-weight-black primary--text lh-1">{{ item.id > -1 ? 'Editar Tipo' : 'Nuevo Tipo' }}</div>
          <div class="caption grey--text font-weight-bold text-uppercase tracking-wider mt-1">Definición de lógica de movimientos de stock</div>
        </div>
      </div>
    </div>

    <div class="pa-8">
      <ValidationObserver v-slot="{ handleSubmit }">
        <v-form @submit.prevent="handleSubmit(save)">
          
          <!-- Section: Movement Direction -->
          <div class="caption primary--text font-weight-black text-uppercase tracking-widest mb-6 d-flex align-center">
            <v-icon x-small color="primary" class="mr-2">fas fa-route</v-icon> Dirección del Movimiento
          </div>

          <v-radio-group v-model="item.in_out" hide-details class="mt-0 modern-radio-group-vertical mb-8">
            <!-- Entrada Card -->
            <v-card 
              outlined 
              class="direction-selection-card pa-4 mb-4 d-flex align-center cursor-pointer transition-fast" 
              :class="item.in_out === true ? 'active-in border-success bg-success-light' : ''" 
              @click="item.in_out = true"
            >
              <v-radio :value="true" color="success" class="mr-4"></v-radio>
              <div class="d-flex flex-column">
                <span class="body-2 font-weight-black mb-1" :class="item.in_out === true ? 'success--text text--darken-2' : ''">Entrada de Inventario</span>
                <span class="caption grey--text lh-1-2">Incrementará las existencias físicas en bodega</span>
              </div>
              <v-spacer></v-spacer>
              <v-avatar size="36" :color="item.in_out === true ? 'success' : 'grey lighten-4'" class="shadow-xs">
                <v-icon small :color="item.in_out === true ? 'white' : 'grey'">fas fa-arrow-up</v-icon>
              </v-avatar>
            </v-card>
            
            <!-- Salida Card -->
            <v-card 
              outlined 
              class="direction-selection-card pa-4 d-flex align-center cursor-pointer transition-fast" 
              :class="item.in_out === false ? 'active-out border-secondary bg-secondary-light' : ''" 
              @click="item.in_out = false"
            >
              <v-radio :value="false" color="secondary" class="mr-4"></v-radio>
              <div class="d-flex flex-column">
                <span class="body-2 font-weight-black mb-1" :class="item.in_out === false ? 'secondary--text' : ''">Salida de Inventario</span>
                <span class="caption grey--text lh-1-2">Restará existencias físicas del inventario actual</span>
              </div>
              <v-spacer></v-spacer>
              <v-avatar size="36" :color="item.in_out === false ? 'secondary' : 'grey lighten-4'" class="shadow-xs">
                <v-icon small :color="item.in_out === false ? 'white' : 'grey'">fas fa-arrow-down</v-icon>
              </v-avatar>
            </v-card>
          </v-radio-group>

          <!-- Section: Details -->
          <div class="caption primary--text font-weight-black text-uppercase tracking-widest mb-6 d-flex align-center">
            <v-icon x-small color="primary" class="mr-2">fas fa-info-circle</v-icon> Detalles y Reglas
          </div>

          <v-row dense class="mb-6">
            <v-col cols="12">
              <ValidationProvider name="Nombre" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="item.name"
                  label="Nombre del Tipo de Movimiento"
                  placeholder="Ej: Consumo por Producción Directa"
                  outlined
                  dense
                  class="modern-input bg-white"
                  :error-messages="errors"
                  :loading="loading"
                  prepend-inner-icon="fas fa-tag"
                />
              </ValidationProvider>
            </v-col>
          </v-row>

          <v-card outlined class="modern-selection-card pa-5 mb-8 transition-fast" :class="item.price_effect ? 'active-selection' : ''">
            <div class="d-flex align-center">
              <v-switch
                v-model="item.price_effect"
                hide-details
                dense
                class="mt-0 pt-0 modern-switch mr-4"
                color="primary"
              ></v-switch>
              <div>
                <div class="body-2 font-weight-black" :class="item.price_effect ? 'primary--text' : 'grey--text text--darken-3'">¿Afecta al Costo Promedio?</div>
                <div class="caption grey--text lh-1-2 mt-1">Determine si este movimiento debe recalcular la valoración económica del artículo.</div>
              </div>
            </div>
          </v-card>

          <!-- Footer Buttons -->
          <div class="d-flex mt-10 gap-3">
            <v-btn 
              type="submit" 
              color="primary" 
              large 
              depressed 
              :loading="loading"
              class="flex-grow-1 py-6 rounded-lg font-weight-black text-uppercase tracking-widest shadow-md"
            >
              <v-icon small class="mr-2">fas fa-save</v-icon>
              {{ item.id > -1 ? 'Actualizar' : 'Crear Tipo' }}
            </v-btn>
            <v-btn 
              text 
              large 
              color="grey darken-1"
              class="rounded-lg font-weight-bold px-8"
              @click.prevent="$emit('close')"
            >
              Cancelar
            </v-btn>
          </div>
        </v-form>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import { getTipo, createTipo, updateTipo } from '@/api/stocks'
import notifications from '@/utils/notifications'

export default {
  name: 'TipoCreateUpdate',
  props: {
    id: { required: true }
  },
  data() {
    return {
      item: { 
        id: -1, 
        name: '', 
        in_out: true, 
        price_effect: false 
      },
      loading: false,
    }
  },
  methods: {
    async save() {
      this.loading = true
      const payload = {
        name: this.item.name,
        in_out: this.item.in_out,
        price_effect: this.item.price_effect
      }
      try {
        if (this.id > -1) {
          await updateTipo(this.id, payload)
          notifications.showSuccess('Tipo de movimiento actualizado')
        } else {
          await createTipo(payload)
          notifications.showSuccess('Nuevo tipo registrado')
        }
        this.$emit('updated')
        this.$emit('close')
      } catch (err) {
        notifications.showError('Error al guardar: ' + err.message)
      } finally {
        this.loading = false
      }
    },
    async get() {
      if (this.id > -1) {
        this.loading = true
        try {
          const res = await getTipo(this.id)
          this.item = res
        } catch (err) {
          notifications.showError('Error al cargar datos')
        } finally {
          this.loading = false
        }
      } else {
        this.item = { id: -1, name: '', in_out: true, price_effect: false }
      }
    }
  },
  watch: {
    id: { handler: 'get', immediate: true }
  }
}
</script>

<style scoped>
.bg-grey-light { background-color: #f8f9fa; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08); }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.2 !important; }
.tracking-widest { letter-spacing: 2px; }
.tracking-wider { letter-spacing: 1px; }
.gap-3 { gap: 12px; }

.modern-input >>> .v-input__control { min-height: 48px !important; }
.modern-input >>> fieldset { border: 1px solid rgba(0,0,0,0.1) !important; border-radius: 8px !important; }
.modern-input >>> .v-input__prepend-inner { margin-top: 12px !important; margin-right: 12px !important; }

.shadow-md { box-shadow: 0 4px 12px rgba(var(--v-primary-base), 0.2) !important; }
.shadow-sm { box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important; }
.shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05) !important; }

.direction-selection-card { border-radius: 12px !important; border: 1.5px solid rgba(0,0,0,0.05) !important; }
.active-in { border-color: var(--v-success-base) !important; background-color: rgba(var(--v-success-base), 0.02) !important; box-shadow: 0 2px 8px rgba(var(--v-success-base), 0.1) !important; }
.active-out { border-color: var(--v-secondary-base) !important; background-color: rgba(var(--v-secondary-base), 0.02) !important; box-shadow: 0 2px 8px rgba(var(--v-secondary-base), 0.1) !important; }

.modern-selection-card { border-radius: 12px !important; border: 1.5px solid rgba(0,0,0,0.05) !important; background-color: #fff; }
.active-selection { border-color: var(--v-primary-base) !important; background-color: rgba(var(--v-primary-base), 0.02) !important; }
.transition-fast { transition: all 0.2s ease; }
.cursor-pointer { cursor: pointer; }
</style>